import type { PublicKey } from '@shared/types';
import { openIndexedDb, transactionDone } from '../utils/idb';

const DB_NAME = 'family-messenger-call-history';
const DB_VERSION = 2;
const STORE_NAME = 'call_history';
const LEGACY_STORAGE_KEY = 'call-history-storage-dev';

export type StoredCallEvent = {
  id: string;
  callSessionId?: string;
  direction: 'incoming' | 'outgoing';
  status: 'ringing' | 'answered' | 'rejected' | 'ended' | 'failed';
  localIdentityId?: string;
  remoteIdentityId: string;
  remoteDisplayName: string;
  remoteIdentityName?: string;
  remotePublicKey?: PublicKey;
  isKnownContact: boolean;
  e2eeVerified: boolean;
  serverId: string;
  serverName: string;
  serverUrl?: string;
  isTemporaryLinkCall?: boolean;
  timestamp: string;
  createdAt?: string;
  connectedAt?: string;
  endedAt?: string;
  reason?: string;
  lastUpdatedAt?: string;
  durationSeconds?: number;
  seen?: boolean;
};

function isIncomingMissedEvent(event: Pick<StoredCallEvent, 'direction' | 'status' | 'durationSeconds'>): boolean {
  return event.direction === 'incoming'
    && (
      event.status === 'failed'
      || (event.status === 'ended' && !event.durationSeconds)
    );
}

function openDB(): Promise<IDBDatabase> {
  return openIndexedDb(DB_NAME, DB_VERSION, (db, request, event) => {
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      store.createIndex('byTimestamp', 'timestamp');
      store.createIndex('byCallSessionId', 'callSessionId');
      store.createIndex('byLastUpdatedAt', 'lastUpdatedAt');
      return;
    }

    if ((event.oldVersion || 0) < 2) {
      const tx = request.transaction;
      if (!tx) return;
      const store = tx.objectStore(STORE_NAME);
      if (!store.indexNames.contains('byLastUpdatedAt')) {
        store.createIndex('byLastUpdatedAt', 'lastUpdatedAt');
      }
    }
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore, tx: IDBTransaction) => void
): Promise<T> {
  const db = await openDB();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, mode);
    const store = tx.objectStore(STORE_NAME);
    fn(store, tx);
    transactionDone(tx)
      .then(() => resolve(undefined as T))
      .catch(reject)
      .finally(() => db.close());
  });
}

function sortNewestFirst(events: StoredCallEvent[]): StoredCallEvent[] {
  return [...events].sort((a, b) => {
    const tsDiff = new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    if (tsDiff !== 0) return tsDiff;
    return b.id.localeCompare(a.id);
  });
}

function readLegacyEvents(): StoredCallEvent[] {
  try {
    const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as { state?: { events?: StoredCallEvent[] } };
    const events = Array.isArray(parsed?.state?.events) ? parsed.state.events : [];
    return sortNewestFirst(events.filter((event) => event && typeof event.id === 'string'));
  } catch {
    return [];
  }
}

export async function getCallHistoryEvents(): Promise<StoredCallEvent[]> {
  const db = await openDB();
  return await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('byTimestamp');
    const req = index.openCursor(null, 'prev');
    const rows: StoredCallEvent[] = [];

    req.onerror = () => {
      db.close();
      reject(req.error);
    };
    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) {
        db.close();
        resolve(rows);
        return;
      }
      rows.push(cursor.value as StoredCallEvent);
      cursor.continue();
    };
  });
}

export async function putCallEvent(event: StoredCallEvent): Promise<void> {
  await withStore<void>('readwrite', (store) => {
    store.put(event);
  });
}

export async function upsertCallEventBySessionId(
  event: Omit<StoredCallEvent, 'id'> & { id?: string }
): Promise<void> {
  const callSessionId = (event.callSessionId || '').trim();
  if (!callSessionId) {
    await putCallEvent({
      ...event,
      id: event.id || `${Date.now()}-${Math.random()}`
    } as StoredCallEvent);
    return;
  }

  await withStore<void>('readwrite', (store, tx) => {
    const index = store.index('byCallSessionId');
    const req = index.openCursor(IDBKeyRange.only(callSessionId));

    req.onerror = () => tx.abort();
    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) {
        store.put({
          ...event,
          id: event.id || `${Date.now()}-${Math.random()}`
        } as StoredCallEvent);
        return;
      }

      const existing = cursor.value as StoredCallEvent;
      cursor.update({
        ...existing,
        ...event,
        id: existing.id,
        remotePublicKey: event.remotePublicKey ?? existing.remotePublicKey,
        seen: existing.seen === true || event.seen === true ? true : event.seen
      });
    };
  });
}

export async function updateCallEventByCallSessionId(
  callSessionId: string,
  updates: Partial<Omit<StoredCallEvent, 'id' | 'timestamp' | 'callSessionId'>>
): Promise<void> {
  if (!callSessionId) return;

  await withStore<void>('readwrite', (store, tx) => {
    const index = store.index('byCallSessionId');
    const req = index.openCursor(IDBKeyRange.only(callSessionId));

    req.onerror = () => tx.abort();
    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) return;
      const existing = cursor.value as StoredCallEvent;
      cursor.update({
        ...existing,
        ...updates
      });
    };
  });
}

export async function markMissedCallEventsSeen(filters?: {
  serverId?: string;
  identityId?: string;
  peerIdentityId?: string;
}): Promise<void> {
  const effectiveServerId = (filters?.serverId || '').trim();
  const effectiveIdentityId = (filters?.identityId || '').trim();
  const effectivePeerIdentityId = (filters?.peerIdentityId || '').trim();

  await withStore<void>('readwrite', (store, tx) => {
    const req = store.openCursor();

    req.onerror = () => tx.abort();
    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) return;

      const event = cursor.value as StoredCallEvent;
      const isIncomingMissed = isIncomingMissedEvent(event);
      const matchesServer = !effectiveServerId || (event.serverId || '').trim() === effectiveServerId;
      const matchesIdentity = !effectiveIdentityId || !event.localIdentityId || (event.localIdentityId || '').trim() === effectiveIdentityId;
      const matchesPeer = !effectivePeerIdentityId || (event.remoteIdentityId || '').trim() === effectivePeerIdentityId;

      if (isIncomingMissed && matchesServer && matchesIdentity && matchesPeer && event.seen !== true) {
        cursor.update({ ...event, seen: true });
      }

      cursor.continue();
    };
  });
}

export async function clearCallHistory(): Promise<void> {
  await withStore<void>('readwrite', (store) => {
    store.clear();
  });
}

export async function deleteCallHistoryForIdentity(params: {
  identityId: string;
  serverId?: string | null;
  serverUrl?: string | null;
}): Promise<void> {
  const target = (params.identityId || '').trim();
  if (!target) return;
  const serverId = (params.serverId || '').trim();
  const serverUrl = (params.serverUrl || '').trim();
  await withStore<void>('readwrite', (store, tx) => {
    const req = store.openCursor();
    req.onerror = () => tx.abort();
    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) return;
      const event = cursor.value as StoredCallEvent;
      const localIdentityId = (event.localIdentityId || '').trim();
      const matchesLegacyCircle = !localIdentityId
        && ((serverId && event.serverId === serverId) || (serverUrl && event.serverUrl === serverUrl));
      if (localIdentityId === target || matchesLegacyCircle) cursor.delete();
      cursor.continue();
    };
  });
}

export async function initializeCallHistoryFromLegacyStorage(): Promise<StoredCallEvent[]> {
  const existing = await getCallHistoryEvents();
  if (existing.length > 0) return existing;

  const legacyEvents = readLegacyEvents();
  if (legacyEvents.length === 0) return [];

  await withStore<void>('readwrite', (store) => {
    for (const event of legacyEvents) {
      store.put(event);
    }
  });

  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    // ignore
  }

  return legacyEvents;
}
